import {useEffect, useState, useRef} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { resumeService } from '../services/resume.service';
import { useAuth } from '../contexts/AuthContext';
import { Resume } from '../types';
import { FileText, Plus, Trash2, Download, Link as LinkIcon } from 'lucide-react';
import { format } from 'date-fns';

export function DashboardPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [resumes, setResumes] = useState<Resume[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isImporting, setIsImporting] = useState(false);
    const [importError, setImportError] = useState('');
    const [importStage, setImportStage] = useState<string>('Uploading file');
    const [showImportOverlay, setShowImportOverlay] = useState(false);
    const [resumeToDelete, setResumeToDelete] = useState<Resume | null>(null);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [copiedResumeId, setCopiedResumeId] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        loadResumes();
    }, []);

    const loadResumes = async () => {
        try {
            const data = await resumeService.getResumes();
            setResumes(data.resumes);
        } catch (err: any) {
            setError('Failed to load resumes');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    const handleCreateResume = async () => {
        try {
            const newResume = await resumeService.createResume({
                title: 'Untitled Resume',
                template: 'ats',
                data: {
                    header: {
                        fullName: 'Your Name',
                        email: user?.email || '',
                        links: [],
                    },
                    experience: [],
                    education: [],
                    skills: { categories: [] },
                    settings: {
                        compactSpacing: false,
                        maxBulletLength: 150,
                    },
                },
            });
            navigate(`/editor/${newResume.id}`);
        } catch (err: any) {
            showToast('Failed to create resume', 'error');
            console.error(err);
        }
    };

    const handleDelete = async (id: string) => {
        const target = resumes.find((r) => r.id === id);
        if (!target) return;
        setResumeToDelete(target);
    };

    const confirmDelete = async () => {
        if (!resumeToDelete) return;
        try {
            await resumeService.deleteResume(resumeToDelete.id);
            setResumes((current) => current.filter((r) => r.id !== resumeToDelete.id));
            showToast('Resume deleted', 'success');
        } catch (err: any) {
            showToast('Failed to delete resume', 'error');
            console.error(err);
        } finally {
            setResumeToDelete(null);
        }
    };

    const cancelDelete = () => setResumeToDelete(null);

    const handleImportClick = () => {
        setImportError('');
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setImportStage('Uploading file');
        setShowImportOverlay(true);
        setIsImporting(true);

        try {
            setImportStage('Analyzing resume');
            const { resume, missingSections } = await resumeService.importResume(file);
            setImportStage('Extracting details');
            setResumes((current) => [resume, ...current]);
            setImportStage('Finalizing');
            navigate(`/editor/${resume.id}`, { state: { missingSections } });
        } catch (err: any) {
            setImportError(err.response?.data?.message || 'Failed to import resume');
            console.error(err);
        } finally {
            setIsImporting(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            setTimeout(() => setShowImportOverlay(false), 600);
        }
    };

    const handleExport = async (id: string, format: 'pdf' | 'docx', title: string) => {
        try {
            const blob = format === 'pdf'
                ? await resumeService.exportPdf(id)
                : await resumeService.exportDocx(id);

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${title}.${format}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err: any) {
            alert(`Failed to export ${format.toUpperCase()}`);
            console.error(err);
        }
    };

    const handleCopyShare = async (resume: Resume) => {
        try {
            const updated = resume.is_public ? resume : await resumeService.shareResume(resume.id);
            setResumes((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
            if (updated.public_id) {
                const url = `${window.location.origin}/r/${updated.public_id}`;
                await navigator.clipboard.writeText(url);
                showToast('Public link copied', 'success');
                setCopiedResumeId(resume.id);
                setTimeout(() => setCopiedResumeId((current) => (current === resume.id ? null : current)), 1500);
            }
        } catch (err: any) {
            console.error(err);
            showToast('Failed to copy link', 'error');
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx"
                className="hidden"
                onChange={handleFileChange}
            />

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <h2 className="text-xl font-semibold text-gray-900">My Resumes</h2>
                <div className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:flex sm:flex-wrap sm:gap-2">
                    <button
                        onClick={handleImportClick}
                        className="btn btn-secondary flex items-center justify-center gap-2 w-full sm:w-auto"
                        disabled={isImporting}
                    >
                        {isImporting ? 'Importing...' : 'Import Resume'}
                    </button>
                    <button
                        onClick={handleCreateResume}
                        className="btn btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
                    >
                        <Plus size={18} />
                        New Resume
                    </button>
                </div>
            </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                        {error}
                    </div>
                )}

                {importError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                        {importError}
                    </div>
                )}

                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    </div>
                ) : resumes.length === 0 ? (
                    <div className="text-center py-12">
                        <FileText size={64} className="mx-auto text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No resumes yet</h3>
                        <p className="text-gray-600 mb-6">Get started by creating your first resume</p>
                        <button onClick={handleCreateResume} className="btn btn-primary">
                            Create Resume
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {resumes.map((resume) => (
                            <div key={resume.id} className="card hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-gray-900 mb-1">{resume.title}</h3>
                                        <p className="text-sm text-gray-500">
                                            Updated {format(new Date(resume.updated_at), 'MMM d, yyyy')}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-1">
                                            Template: {resume.template === 'ats' ? 'ATS-Friendly' : 'Modern'}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => handleDelete(resume.id)}
                                        className="text-red-600 hover:text-red-700 p-2"
                                        title="Delete"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>

                                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                                    <Link
                                        to={`/editor/${resume.id}`}
                                        className="btn btn-primary w-full text-center text-sm sm:w-auto"
                                    >
                                        Edit
                                    </Link>
                                    {resume.public_id && (
                                        <button
                                            onClick={() => handleCopyShare(resume)}
                                            className="btn btn-secondary w-full flex items-center justify-center gap-1 text-sm sm:w-auto"
                                            title="Copy share link"
                                        >
                                            <LinkIcon size={16} />
                                            {copiedResumeId === resume.id ? 'Copied' : 'Link'}
                                        </button>
                                    )}
                                    {!resume.public_id && (
                                        <button
                                            onClick={() => handleCopyShare(resume)}
                                            className="btn btn-secondary w-full flex items-center justify-center gap-1 text-sm sm:w-auto"
                                            title="Create and copy share link"
                                        >
                                            <LinkIcon size={16} />
                                            {copiedResumeId === resume.id ? 'Copied' : 'Link'}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleExport(resume.id, 'pdf', resume.title)}
                                        className="btn btn-secondary w-full flex items-center justify-center gap-1 text-sm sm:w-auto"
                                        title="Download PDF"
                                    >
                                        <Download size={16} />
                                        PDF
                                    </button>
                                    <button
                                        onClick={() => handleExport(resume.id, 'docx', resume.title)}
                                        className="btn btn-secondary w-full flex items-center justify-center gap-1 text-sm sm:w-auto"
                                        title="Download DOCX"
                                    >
                                        <Download size={16} />
                                        DOCX
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            {showImportOverlay && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg w-80 p-6 shadow-lg flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary-600"></div>
                        <div className="text-lg font-semibold">Importing resume</div>
                        <p className="text-sm text-gray-600 text-center">{importStage}</p>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-primary-600 h-2 rounded-full" style={{ width: '50%' }} />
                        </div>
                    </div>
                </div>
            )}

            {toast && (
                <div
                    className={`fixed right-6 bottom-6 px-4 py-2 rounded shadow-lg z-50 ${toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                        }`}
                >
                    {toast.message}
                </div>
            )}

            {resumeToDelete && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg p-6 w-96 shadow-lg space-y-3">
                        <h3 className="text-lg font-semibold">Delete resume</h3>
                        <p className="text-sm text-gray-600">
                            Are you sure you want to delete <span className="font-semibold">{resumeToDelete.title}</span>? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button onClick={cancelDelete} className="btn btn-secondary">
                                Cancel
                            </button>
                            <button onClick={confirmDelete} className="btn btn-danger">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
